import { useCallback, useMemo, useRef, useState } from "react";

type UndoRedoOptions<T> = {
  capacity?: number;
  /**
   * Optional custom equality comparator. If it returns true, the new value will not be pushed to history.
   */
  isEqual?: (a: T, b: T) => boolean;
  /**
   * Optional deep-clone function. Defaults to structuredClone (if available) then JSON serialize/parse.
   */
  clone?: (value: T) => T;
};

export type UndoRedoApi<T> = {
  /** Current value */
  value: T;
  /** Replace the current value and push a new snapshot to history (truncates future if not at the end). */
  set: (next: T) => void;
  /** Replace the current value WITHOUT adding to history (useful for programmatic sync). */
  replaceCurrent: (next: T) => void;
  /** Reset the history to a single snapshot (index=0). */
  reset: (initial: T) => void;
  /** Move one step back in history; returns the new value or null if not possible. */
  undo: () => T | null;
  /** Move one step forward in history; returns the new value or null if not possible. */
  redo: () => T | null;
  /** Whether undo is currently possible */
  canUndo: boolean;
  /** Whether redo is currently possible */
  canRedo: boolean;
  /** History length (snapshots count) */
  length: number;
  /** Current index within history (0-based) */
  index: number;
};

function defaultClone<T>(value: T): T {
  // Prefer structuredClone if available
  try {
    const sc = (globalThis as unknown as { structuredClone?: (v: T) => T })
      .structuredClone;
    if (typeof sc === "function") return sc(value);
  } catch {
    // fall back
  }
  // Fallback to JSON serialization for plain data
  try {
    return JSON.parse(JSON.stringify(value)) as T;
  } catch {
    return value;
  }
}

function defaultEqual<T>(a: T, b: T): boolean {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return a === b;
  }
}

export function useUndoRedo<T>(initial: T, options?: UndoRedoOptions<T>): UndoRedoApi<T> {
  const opt = options || {};
  const capacity = Math.max(1, Math.floor(opt.capacity ?? 100));
  const clone = opt.clone ?? defaultClone<T>;
  const isEqual = opt.isEqual ?? defaultEqual<T>;

  const [state, setState] = useState<{ history: T[]; index: number }>(() => ({
    history: [clone(initial)],
    index: 0,
  }));

  // Keep a ref of the latest state to avoid stale closures in callbacks
  const stateRef = useRef(state);
  stateRef.current = state;

  const value = useMemo(() => state.history[state.index], [state.history, state.index]);

  const replaceCurrent = useCallback(
    (next: T) => {
      setState((prev) => {
        const snap = clone(next);
        const history = [...prev.history];
        history[prev.index] = snap;
        return { history, index: prev.index };
      });
    },
    [clone],
  );

  const set = useCallback(
    (next: T) => {
      setState((prev) => {
        const current = prev.history[prev.index];
        if (isEqual(current, next)) return prev; // ignore no-ops

        // Truncate future if not at the end
        const base = prev.history.slice(0, prev.index + 1);
        base.push(clone(next));
        // Enforce capacity by dropping oldest
        const overflow = Math.max(0, base.length - capacity);
        const pruned = overflow > 0 ? base.slice(overflow) : base;
        const newIndex = pruned.length - 1;
        return { history: pruned, index: newIndex };
      });
    },
    [clone, capacity, isEqual],
  );

  const reset = useCallback(
    (initialValue: T) => {
      setState({ history: [clone(initialValue)], index: 0 });
    },
    [clone],
  );

  const undo = useCallback((): T | null => {
    const { index, history } = stateRef.current;
    if (index <= 0) return null;
    const nextIndex = index - 1;
    setState((prev) => ({ history: prev.history, index: nextIndex }));
    return history[nextIndex];
  }, []);

  const redo = useCallback((): T | null => {
    const { index, history } = stateRef.current;
    if (index >= history.length - 1) return null;
    const nextIndex = index + 1;
    setState((prev) => ({ history: prev.history, index: nextIndex }));
    return history[nextIndex];
  }, []);

  const canUndo = state.index > 0;
  const canRedo = state.index < state.history.length - 1;
  const length = state.history.length;
  const index = state.index;

  return {
    value,
    set,
    replaceCurrent,
    reset,
    undo,
    redo,
    canUndo,
    canRedo,
    length,
    index,
  };
}

export default useUndoRedo;
